import { useState, useEffect } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { Loader2, ExternalLink } from "lucide-react";
import api from "../services/api";
import { getAdminKingsChatLoginUrl } from "../lib/config";
import { useAuth } from "../contexts/AuthContext";

export function AdminLogin() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Already logged in as admin → go to admin dashboard
  if (!authLoading && isAuthenticated && user?.type === "admin") {
    return <Navigate to="/pilot/dashboard" replace />;
  }

  // Force light mode on admin login page
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    return () => {
      // Don't restore theme on unmount - let ThemeProvider handle it after login
    };
  }, []);

  useEffect(() => {
    // Check for token from backend redirect
    const token = searchParams.get('token');
    const isAdmin = searchParams.get('isAdmin');
    const errorParam = searchParams.get('error');
    const messageParam = searchParams.get('message');
    const redirect = searchParams.get('redirect');

    // Store redirect parameter if provided
    if (redirect) {
      localStorage.setItem('intended_redirect', redirect);
    }

    if (errorParam) {
      setError(decodeURIComponent(messageParam || 'Authentication failed'));
      // Clear URL params
      window.history.replaceState({}, document.title, '/pilot/login');
      return;
    }

    if (token && isAdmin === 'true') {
      handleTokenLogin(token);
    }
  }, [searchParams]);

  const handleTokenLogin = async (token) => {
    setRedirecting(true);
    setLoading(true);
    setError("");

    try {
      // Store token
      localStorage.setItem('auth_token', token);

      // Fetch admin data
      const response = await api.get('/me');
      
      if (response.data.success && response.data.data) {
        const adminData = { ...response.data.data, type: 'admin' };
        localStorage.setItem('user', JSON.stringify(adminData));
        
        // Update AuthContext state immediately so isAuthenticated becomes true
        // Note: AdminLogin doesn't use AuthContext directly, but we need to trigger a state update
        // The AuthContext will pick it up on next render, but we can also force a reload
        window.dispatchEvent(new Event('storage'));
        
        // Get redirect path from URL or localStorage, default to admin dashboard
        // Ensure admin only redirects to admin routes (not staff routes)
        let redirectPath = searchParams.get('redirect') || localStorage.getItem('intended_redirect') || '/pilot/dashboard';
        localStorage.removeItem('intended_redirect');
        
        // Validate redirect path - admin should not access staff routes
        if (!redirectPath.startsWith('/pilot')) {
          redirectPath = '/pilot/dashboard'; // Force admin to dashboard if trying to access staff route
        }
        
        // Clear URL params and redirect
        window.history.replaceState({}, document.title, '/pilot/login');
        
        // Use window.location for admin to ensure full state refresh
        window.location.href = redirectPath;
      } else {
        setError('Failed to fetch admin data');
        setRedirecting(false);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to authenticate';
      setError(errorMessage);
      console.error('Token login error:', err);
      setRedirecting(false);
      // Clear URL params on error
      window.history.replaceState({}, document.title, '/pilot/login');
    } finally {
      setLoading(false);
    }
  };

  const handleKingsChatLogin = () => {
    // Get intended redirect from URL or localStorage
    const redirect = searchParams.get('redirect') || localStorage.getItem('intended_redirect') || '/pilot/dashboard';
    if (redirect) {
      localStorage.setItem('intended_redirect', redirect);
    }
    // Redirect to API web route GET /admin/login (adminLoginLink) → KingsChat OAuth
    window.location.href = getAdminKingsChatLoginUrl(redirect || undefined);
  };

  // Generate animated circles
  const circles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    size: Math.random() * 200 + 50,
    left: Math.random() * 100,
    top: Math.random() * 100,
    delay: Math.random() * 10,
    duration: Math.random() * 20 + 15,
  }));

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#1558D6] via-[#2D7AFF] to-[#5B9AFF] p-4 relative overflow-hidden">
      {/* Animated Background Circles */}
      <div className="absolute inset-0 overflow-hidden">
        {circles.map((circle) => (
          <div
            key={circle.id}
            className="absolute rounded-full opacity-35 animate-float"
            style={{
              width: `${circle.size}px`,
              height: `${circle.size}px`,
              left: `${circle.left}%`,
              top: `${circle.top}%`,
              background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 70%)',
              animationDelay: `${circle.delay}s`,
              animationDuration: `${circle.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 relative z-10">
        {/* Logo and Title */}
        <div className="flex flex-col items-center mb-8">
          <div className="mb-4">
            <div className="size-20 rounded-2xl bg-white p-2 flex items-center justify-center shadow-lg shadow-[#1E74FD]/50 ring-2 ring-[#1E74FD]/20">
              <img src="/favicon.png" alt="LW Staff Portal" className="size-full object-contain" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">LoveWorld Staff Portal</h1>
          <p className="text-gray-600">Administrator login only</p>
        </div>

        {/* Card Content */}
        <div>
          {redirecting ? (
            /* Redirecting Card */
            <div className="flex flex-col items-center justify-center py-12">
              <div className="mb-6">
                <div className="size-20 rounded-2xl bg-white p-2 flex items-center justify-center shadow-lg shadow-[#1E74FD]/50 mx-auto ring-2 ring-[#1E74FD]/20">
                  <img src="/favicon.png" alt="" className="size-full object-contain" aria-hidden />
                </div>
                <Loader2 className="size-8 text-[#1E74FD] animate-spin mx-auto mt-4" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Redirecting...</h2>
              <p className="text-gray-600 text-center">Please wait while we log you in</p>
            </div>
          ) : (
            <>
              {/* Login Form */}
              <div className="space-y-5">
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-2">
                    <div className="size-1.5 rounded-full bg-red-500 animate-pulse" />
                    {error}
                  </div>
                )}

                {loading && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-xl flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Authenticating with KingsChat...
                  </div>
                )}

                {/* KingsChat Login Button */}
                <button
                  type="button"
                  onClick={handleKingsChatLogin}
                  disabled={loading}
                  className="w-full bg-[#1E74FD] text-white py-3 rounded-xl font-semibold hover:bg-[#1558D6] transition-colors shadow-lg shadow-[#1E74FD]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  <img 
                    src="https://kingsch.at/h/css/images/favicon.ico" 
                    alt="KingsChat" 
                    className="size-5"
                  />
                  <span>Login with KingsChat</span>
                  <ExternalLink className="size-4 opacity-80" />
                </button>

                <div className="mt-6 text-center text-sm text-gray-600">
                  <a href="/login" className="text-[#1E74FD] hover:underline transition-all hover:scale-105 inline-block">
                    Staff Login
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          33% {
            transform: translateY(-30px) translateX(20px);
          }
          66% {
            transform: translateY(30px) translateX(-20px);
          }
        }
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </div>
  );
}
